/**
 * A MARK LEFT ON ONE SIGN-IN IS HER MARK ON THE OTHER: READ-SIDE ONLY.
 *
 * Bobbie, 2026-09-07: *"The two types of jr staff should act exactly the same
 * way, zero difference other than how they are set up."*
 *
 * Almost every "have you dealt with this yet" fact in the Hub is a map keyed
 * by uid - readBy on a message, acks and dismissedBy on a post or a board
 * discussion, reactions. A Jr Staff holding two live sign-ins therefore did
 * her work twice: she read a group thread on her email account and it was
 * still bold on her dancer sign-in, she acked a class post on one and it
 * asked her again on the other.
 *
 * THIS IS RECONCILIATION, NOT A WRITE. Everything here is the READ side: any
 * of her uids carrying a mark means the human has dealt with it. The WRITE
 * side stays keyed on the uid actually signed in, on purpose and permanently:
 * shared/firestore.rules permits only the caller's own slot in each of these
 * maps, so a client writing under her other uid is refused - and a screen
 * that offered the write anyway would be drawing a door the rule slams.
 *
 * COSTS ZERO RULES BYTES. Every map read here rides on a document the viewer
 * was already allowed to read; nothing widens what she can open.
 *
 * WHAT STILL SPLITS, named rather than quietly left broken:
 *
 *   1. users/{uid}/messageInboxState - the "Done with this" filing for
 *      threads, announcements, bulletins, sub requests, class notes and,
 *      since 2026-09-14, attendance concerns (and with them the staff home's
 *      concern count, which follows the filing). It is a
 *      SUBCOLLECTION UNDER HER OWN USER DOCUMENT, and the rules scope it to
 *      request.auth.uid, so her other account's filing is not readable by her
 *      at all. There is no zero-byte reconciliation for it: it needs either a
 *      rules branch admitting a linked uid, or a server lane that merges the
 *      two. Filing on one sign-in therefore still leaves the item open on the
 *      other.
 *
 *   2. notifPrefs.mutedIds IS NOW RECONCILED, DIFFERENTLY FROM EVERYTHING
 *      ELSE ON THIS LIST (packet J30; DECISIONS.md entries 26 and 27 -
 *      "A Jr Staff is a parent who is also a teacher: zero difference by
 *      sign-up kind, and every sign-in gets everything" - hold the golden
 *      rule this applies. THE RULING ITSELF is quoted rather than numbered:
 *      DECISIONS.md's dated 2026-09-08 list once gave it a number in the
 *      thirties, and that same number has since been reused by an
 *      unrelated later entry, so citing it bare no longer points anywhere
 *      reliable - Bobbie, 2026-09-08
 *      03:58 UTC, verbatim: "Yes they should be exactly the same." A mute
 *      set on one of her sign-ins applies on the other, stored against the
 *      canonical person and read the same way on both, never two copies
 *      that can drift. The 2026-09-17 notification audit's finding B9
 *      corrected an earlier version of THIS comment that wrongly said
 *      nobody had ruled - the ruling always existed; what changed here is
 *      that packet J30 is now BUILT, not merely ruled on.
 *
 *      A first version of the fix belonged right here - a read-side merge,
 *      "whichever sign-in wrote more recently wins" - and Fable's refute
 *      round on PR #1148 proved it wrong by execution: a `set` on one
 *      sign-in stamped ITS OWN doc newer without knowing what the other one
 *      said, so the next `set` on the OTHER sign-in silently resurrected a
 *      stale value, and an UNRELATED updatedAt bump (a push-token
 *      enrollment, a display-name edit, roughly twenty other self-writers)
 *      could flip which doc looked newer with no mute change at all.
 *      mutedIds is therefore NOT a read-side reconciliation like items 1
 *      and 3 - it is a single canonical value, kept in sync by a WRITE that
 *      lands on every one of her sign-ins' docs in one transaction:
 *      netlify/functions/lib/boardNotificationLevel.mjs, called from
 *      lib/users.ts's setMyBoardNotificationLevel. Her own doc is simply
 *      already correct to read, on either sign-in, which is why
 *      BoardControls.tsx's BoardNotificationsButton reads it directly with
 *      no merge of its own. There is no delivery-side safety net reading
 *      two of her docs any more either - a second reviewer's sweep on
 *      2026-09-16 removed the one notificationAuthority.mjs used to keep,
 *      because it billed an unbudgeted query per recipient and could
 *      re-bill a whole transaction chunk on retry. What keeps her docs
 *      correct without that lookup is the canonical write itself, the
 *      once-per-session client reconcile above, and a scheduled sweep pass
 *      (netlify/functions/lib/jrStaffMuteReconcileSweep.mjs) that closes
 *      drift for anyone who never opens a board at all.
 *
 *   3. The write side of items 1 above, by design - see the second
 *      paragraph. Unlike item 2, those stay keyed on the uid actually
 *      signed in; only their READ side widens.
 */

/** A viewer as one uid or as every sign-in she holds. */
export type PerPersonViewer = string | readonly string[] | null | undefined;

/**
 * Normalise a viewer to a clean uid list. An empty result means "nobody",
 * never "everybody": every predicate below answers false for it.
 */
export function myUids(viewer: PerPersonViewer): string[] {
  if (viewer == null) return [];
  const raw = typeof viewer === 'string' ? [viewer] : [...viewer];
  return [...new Set(
    raw.map((uid) => (typeof uid === 'string' ? uid.trim() : '')).filter((uid) => uid.length > 0)
  )];
}

/**
 * Did THIS HUMAN mark this, under any of her sign-ins? The one spelling of
 * `map?.[uid]` for readBy, acks, dismissedBy and reactions alike, so the four
 * of them can never end up disagreeing about the same person.
 */
export function markedByAnyOfMine(
  marks: Readonly<Record<string, unknown>> | null | undefined,
  viewer: PerPersonViewer
): boolean {
  if (!marks) return false;
  return myUids(viewer).some((uid) => Boolean(marks[uid]));
}

/** Is this message from THIS HUMAN, on either of her sign-ins? */
export function authoredByMe(
  authorUid: string | null | undefined,
  viewer: PerPersonViewer
): boolean {
  return typeof authorUid === 'string' && myUids(viewer).includes(authorUid.trim());
}

